import type React from 'react';
import { useContext } from 'react';
import ReactDOM from 'react-dom';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

import { FlowPopup } from 'components/molecules/LexicalEditor/plugins/ActionPicker/components/FlowPopup';
import { FlowStepsPopup } from 'components/molecules/LexicalEditor/plugins/ActionPicker/components/FlowStepsPopup';
import { FunctionCreatePopup } from 'components/molecules/LexicalEditor/plugins/ActionPicker/components/FunctionCreatePopup';
import { HandoffPopup } from 'components/molecules/LexicalEditor/plugins/ActionPicker/components/HandoffPopup';
import { StepSetupPopup } from 'components/molecules/LexicalEditor/plugins/ActionPicker/components/StepSetupPopup';
import { TransitionPopup } from 'components/molecules/LexicalEditor/plugins/ActionPicker/components/TransitionPopup';
import {
  MenuOptions,
  SetupOptions,
} from 'components/molecules/LexicalEditor/plugins/ActionPicker/types';
import EntityPopup from '../EntityPopup';
import ExitFlowPopup from '../FlowExitPopup';
import Sms from '../Sms';
import { VariableCreatePopup } from '../VariablePopup';
import VariantAttribute from '../VariantAttribute';
import { CreateActionContext } from './provider';
import type { CreateActionPopupProps } from './types';

export const ACTION_TAG_WATERMARK = 'action_tag_watermark';

/**
 * Acts as a flag for the `FormProvider` to know when not to submit the form on blur
 * @return {(React.JSX.Element|null)} React element or null
 */
const WatermarkPortal = () => {
  const { activePopup } = useContext(CreateActionContext);
  const [editor] = useLexicalComposerContext();
  const isPopupActive = activePopup !== undefined;

  if (!isPopupActive) {
    return null;
  }

  return ReactDOM.createPortal(
    <div id={ACTION_TAG_WATERMARK + editor._config.namespace} />,
    document.body,
  );
};

const CreateActionContent = ({
  children,
  type,
}: {
  children?: React.ReactNode;
  type: MenuOptions | SetupOptions;
}) => {
  const { activePopup } = useContext(CreateActionContext);
  if (activePopup !== type) {
    return null;
  }

  return <>{children}</>;
};

const CreateActionPopup: React.FC<CreateActionPopupProps> = ({
  resolve,
  reject,
  option,
}) => {
  return (
    <>
      <WatermarkPortal />
      <CreateActionContent type={MenuOptions.FUNCTIONS}>
        <FunctionCreatePopup onCancel={reject} onCreate={resolve} />
      </CreateActionContent>
      <CreateActionContent type={MenuOptions.SMS}>
        <Sms onCancel={reject} onCreate={resolve} />
      </CreateActionContent>
      <CreateActionContent type={MenuOptions.VARIANT_ATTRIBUTES}>
        <VariantAttribute onCancel={reject} onCreate={resolve} />
      </CreateActionContent>
      <CreateActionContent type={MenuOptions.HANDOFF}>
        <HandoffPopup onCancel={reject} onCreate={resolve} />
      </CreateActionContent>
      <CreateActionContent type={MenuOptions.TRANSITION_FUNCTION}>
        <TransitionPopup onCancel={reject} onCreate={resolve} />
      </CreateActionContent>
      <CreateActionContent type={MenuOptions.STEPS}>
        <FlowStepsPopup onCancel={reject} onCreate={resolve} />
      </CreateActionContent>
      <CreateActionContent type={MenuOptions.FLOWS}>
        <FlowPopup onCancel={reject} onCreate={resolve} />
      </CreateActionContent>
      <CreateActionContent type={SetupOptions.EXIT_FLOW}>
        <ExitFlowPopup onCancel={reject} onCreate={resolve} />
      </CreateActionContent>
      <CreateActionContent type={SetupOptions.STEPS}>
        <StepSetupPopup option={option} onCancel={reject} onCreate={resolve} />
      </CreateActionContent>
      <CreateActionContent type={MenuOptions.ENTITIES}>
        <EntityPopup onCancel={reject} onCreate={resolve} />
      </CreateActionContent>
      <CreateActionContent type={MenuOptions.VARIABLES}>
        <VariableCreatePopup onCancel={reject} onCreate={resolve} />
      </CreateActionContent>
    </>
  );
};

export { CreateActionPopup };
